// web/render.ts — Canvas 2D view of the live simulation. Reads the engine's
// Structure-of-Arrays columns directly (no per-entity objects) and draws:
//   walls · food (sized by remaining amount) · organisms (colour = colony,
//   shape = caste) · founded hive nodes · the player's nest ring.
//
// Territory/region control is REGION-level (not tile-level) in the sim, so it is
// surfaced in the HUD, not painted onto the grid — the canvas shows the physical
// colony world honestly.

import { KIND } from '../src/types.ts';
import type { Caste } from '../src/types.ts';
import { buildCreatureGeometry, paintCreature } from './creatureDraw.ts';
import { drawLandmarks } from './landmarks.ts';
import { PLAYER_COLONY } from './game.ts';
import type { GameSession } from './game.ts';
import type { Settings } from './save.ts';
import type { LogisticsView } from './logistics.ts';
import type { WatchView } from './watch.ts';
import type { ExplorationMap } from './fog.ts';

/**
 * Per-species draw style, indexed by SPECIES.* (Spider, Bird, Rat, Frog, Beetle,
 * Dragonfly). A NEUTRAL/HOSTILE palette — deliberately NOT colony colours — so
 * wildlife is never mistaken for the player's bugs. `flying` species draw a
 * lighter winged mark; ground species are solid filled shapes.
 *
 *   shape: 'spider' | 'bird' | 'rat' | 'frog' | 'beetle' | 'dragonfly'
 */
interface SpeciesStyle {
  readonly color: string;
  readonly shape: 'spider' | 'bird' | 'rat' | 'frog' | 'beetle' | 'dragonfly';
}
const SPECIES_STYLE: readonly SpeciesStyle[] = [
  { color: '#9b8cff', shape: 'spider' },    // Spider — venom violet
  { color: '#d7e3f0', shape: 'bird' },      // Bird — pale sky (flying)
  { color: '#a8794e', shape: 'rat' },       // Rat — muddy brown
  { color: '#6fd08a', shape: 'frog' },      // Frog — swamp green
  { color: '#8a7a52', shape: 'beetle' },    // Beetle — armoured tan (harmless)
  { color: '#7fe6e0', shape: 'dragonfly' }, // Dragonfly — iridescent teal (flying)
];

/** Apex boss draw colour, indexed by APEX_KIND.* (Huntsman, Crow, RatKing, Pond). */
const APEX_COLOR: readonly string[] = ['#ff4d6d', '#c77dff', '#ff8b3d', '#4dd0a0'];

/** Apex FSM mode label, indexed by APEX_MODE.* (Patrol/Hunt/Retreat/Migrate). */
const APEX_MODE_NAME: readonly string[] = ['Patrol', 'Hunt', 'Retreat', 'Migrate'];
/** Dash pattern (screen px) for the apex hunting-territory ring — a module const so the
 *  per-frame draw allocates nothing. */
const APEX_TERRITORY_DASH: readonly number[] = [5, 4];

/** Reused per-frame scratch: fog tile indices that had VISIBLE food this frame. Lets the
 *  remembered-food pass tell "revisited & still there" from "revisited & now empty" without
 *  a per-frame allocation. */
const rememberedFoodSeen = new Set<number>();

/**
 * FULL-DETAIL DRAW BUDGET: the max number of layered-creature bugs painted at full
 * geometry per frame. `buildCreatureGeometry` allocates primitive arrays per drawn
 * bug and `paintCreature` issues ~10-15 canvas ops each, so a zoomed-in view with
 * hundreds of full-detail bugs could GC-churn / fill-rate bind. Once this many
 * have been drawn at detail in a frame, the rest fall back to the cheap LOD dot
 * path. Selection is by ACTIVE-SET ORDER (the first N alive ids), which is stable
 * frame-to-frame, so the same bugs stay full-detail and there is no flicker.
 */
const FULL_DETAIL_BUDGET = 240;

/** Colony colours. Index 0 = the player (bright); the rest are rivals. */
const COLONY_COLORS: readonly string[] = [
  '#46e0d0', // player — cyan
  '#ff5d5d', // rival — red
  '#ffb13d', // rival — amber
  '#b06bff', // rival — violet
  '#5dd65d', // rival — green
  '#ff6fd0', // rival — pink
];

function colonyColor(cid: number): string {
  if (cid < 0) return '#888';
  return COLONY_COLORS[cid % COLONY_COLORS.length] as string;
}

/** Fill tint per CHAMBER.* type (Nursery / Storage / Military / Mutation). */
const CHAMBER_COLOR: readonly string[] = ['#e08fb0', '#e0b341', '#e05d5d', '#b06bff'];

/** Atmospheric full-map wash per ECO_EVENT.* (index 0 = None ⇒ '' ⇒ no wash). Low-alpha
 *  so it tints the mood without obscuring play: Pesticide green, HeatWave orange,
 *  Flooding blue, PredatorMigration blood-red, FoodSurplusBloom lush green. */
const EVENT_TINT: readonly string[] = [
  '',                       // None
  'rgba(124,176,72,0.10)',  // PesticideOutbreak
  'rgba(255,140,48,0.10)',  // HeatWave
  'rgba(60,130,220,0.12)',  // Flooding
  'rgba(150,60,90,0.10)',   // PredatorMigration
  'rgba(120,210,140,0.09)', // FoodSurplusBloom
];

/** Generation overlay ramp by AGE (currentGen - gen): index 0 = newest brood (brightest), older = cooler/dimmer. Age beyond the last stop clamps to the oldest tone. */
const GENERATION_RAMP: readonly string[] = ['#f9f871', '#7dd66b', '#46c0c0', '#5a86d8', '#7a6fc0', '#5b5570'];
function generationColor(age: number): string {
  const i = age < 0 ? 0 : age >= GENERATION_RAMP.length ? GENERATION_RAMP.length - 1 : age;
  return GENERATION_RAMP[i] as string;
}

export interface ViewBox {
  cell: number;
  offX: number;
  offY: number;
}

/** Compute square cell size + centering offsets to fit the grid in the canvas. */
function fit(cssW: number, cssH: number, gridW: number, gridH: number): ViewBox {
  const cell = Math.max(2, Math.floor(Math.min(cssW / gridW, cssH / gridH)));
  const offX = Math.floor((cssW - cell * gridW) / 2);
  const offY = Math.floor((cssH - cell * gridH) / 2);
  return { cell, offX, offY };
}

/**
 * Draw one frame. `cssW`/`cssH` are CSS pixels; the caller has already scaled the
 * context by devicePixelRatio.
 */
export function render(
  ctx: CanvasRenderingContext2D,
  cssW: number,
  cssH: number,
  session: GameSession,
  settings: Settings,
  logistics?: LogisticsView | null,
  watch?: WatchView | null,
  fog?: ExplorationMap | null,
): ViewBox {
  const world = session.world;
  const grid = world.grid;
  const entities = world.entities;
  const W = grid.width;
  const H = grid.height;
  const view = watch ? watch.viewbox(cssW, cssH, W, H) : fit(cssW, cssH, W, H);
  const { cell, offX, offY } = view;

  // Visible tile RANGE [x0,x1) × [y0,y1): the slice of the grid actually on-canvas,
  // clipped to bounds. On the big map this bounds every per-tile/per-entity loop to
  // what the camera can see, so cost tracks the viewport, not the 13k-tile world.
  let x0 = Math.floor(-offX / cell); if (x0 < 0) x0 = 0;
  let y0 = Math.floor(-offY / cell); if (y0 < 0) y0 = 0;
  let x1 = Math.ceil((cssW - offX) / cell); if (x1 > W) x1 = W;
  let y1 = Math.ceil((cssH - offY) / cell); if (y1 > H) y1 = H;

  // Background.
  ctx.fillStyle = '#0d1117';
  ctx.fillRect(0, 0, cssW, cssH);

  // Playfield backdrop (only the visible span when fog is on; whole field otherwise).
  ctx.fillStyle = '#12161d';
  ctx.fillRect(offX, offY, cell * W, cell * H);

  // Optional grid lines (clipped to the visible range to stay cheap when zoomed in).
  if (settings.showGrid && cell >= 6) {
    ctx.strokeStyle = 'rgba(255,255,255,0.04)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    for (let x = x0; x <= x1; x++) {
      ctx.moveTo(offX + x * cell + 0.5, offY + y0 * cell);
      ctx.lineTo(offX + x * cell + 0.5, offY + y1 * cell);
    }
    for (let y = y0; y <= y1; y++) {
      ctx.moveTo(offX + x0 * cell, offY + y * cell + 0.5);
      ctx.lineTo(offX + x1 * cell, offY + y * cell + 0.5);
    }
    ctx.stroke();
  }

  // Walls (clipped to the visible range; skip walls the player has never discovered
  // — the fog cover below will black them out anyway).
  ctx.fillStyle = '#2b3340';
  for (let y = y0; y < y1; y++) {
    for (let x = x0; x < x1; x++) {
      if (fog && !fog.isExplored(x, y)) continue;
      if (!grid.isPassable(x, y)) ctx.fillRect(offX + x * cell, offY + y * cell, cell, cell);
    }
  }

  // Named LANDMARKS — colour-coded influence zones + iconic markers, painted over
  // the terrain but UNDER the live colony/wildlife so important places (food
  // larders, wildlife territories, barriers) are instantly recognisable.
  drawLandmarks(ctx, world, view, fog);

  // Logistics layers (under everything live), both gated by showTrails:
  //   • persistent tiered SUPPLY ROUTES (Secondary/Primary/Major) — sustained
  //     traffic hardened into a hierarchy, painted first so it underlays;
  //   • the live pheromone heatmap glow on top — where carriers are right now.
  if (logistics && settings.showTrails) {
    logistics.drawRoutes(ctx, view);
    logistics.drawTrails(ctx, view);
  }

  const active = entities.activeView();
  const n = entities.aliveCount;

  // Food nodes (green, radius scales gently with remaining amount). Food is a LIVE
  // ecosystem resource: only shown where the player can currently sense it (you
  // don't track food you can't see). Always shown when fog is off.
  ctx.fillStyle = '#3fae54';
  if (fog) rememberedFoodSeen.clear();
  for (let i = 0; i < n; i++) {
    const id = active[i] as number;
    if ((entities.kind[id] as number) !== KIND.Food) continue;
    const amt = entities.foodAmount[id] as number;
    if (amt <= 0) continue;
    const px = entities.posX[id] as number;
    const py = entities.posY[id] as number;
    if (fog && !fog.isVisible(px, py)) continue;
    if (fog) { fog.noteFood(px, py, amt); rememberedFoodSeen.add(py * fog.width + px); }
    const cx = offX + px * cell + cell / 2;
    const cy = offY + py * cell + cell / 2;
    const r = Math.max(1.2, Math.min(cell * 0.45, 1.5 + amt * 0.18));
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fill();
  }
  // Remembered-food markers: faint dots where the player LAST saw food that is now hidden
  // under fog (you remember where a patch was). A revisit that finds the tile empty
  // (visible but not noted this frame) forgets it, so stale ghosts don't linger. The whole
  // memory is render-only — fog.remFood is never saved or hashed.
  if (fog) {
    ctx.fillStyle = 'rgba(63,174,84,0.30)';
    for (const [k, amt] of fog.remFood) {
      const fx = k % fog.width;
      const fy = (k / fog.width) | 0;
      if (fog.isVisible(fx, fy)) {
        if (!rememberedFoodSeen.has(k)) fog.remFood.delete(k); // revisited & empty ⇒ forget
        continue;                                              // visible ⇒ the live layer drew it
      }
      if (!fog.isExplored(fx, fy)) continue;
      const cx = offX + fx * cell + cell / 2;
      const cy = offY + fy * cell + cell / 2;
      const rr = Math.max(1, Math.min(cell * 0.3, 1 + amt * 0.12));
      ctx.beginPath();
      ctx.arc(cx, cy, rr, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  // Storage granaries at each nest, filling with the colony's food stockpile.
  if (logistics) logistics.drawStorage(ctx, view, session);

  // Hive nodes (founded expansion points).
  if (settings.showHiveNodes) {
    for (let cid = 0; cid < world.colonies.count; cid++) {
      const hives = world.ai.hiveCountAt(cid);
      if (hives === 0) continue;
      ctx.strokeStyle = colonyColor(cid);
      ctx.lineWidth = Math.max(1, cell * 0.12);
      for (let k = 0; k < hives; k++) {
        const hx = world.ai.hiveXAt(cid, k);
        const hy = world.ai.hiveYAt(cid, k);
        const cx = offX + hx * cell + cell / 2;
        const cy = offY + hy * cell + cell / 2;
        const r = Math.max(2, cell * 0.42);
        ctx.beginPath();
        ctx.moveTo(cx, cy - r); ctx.lineTo(cx + r, cy);
        ctx.lineTo(cx, cy + r); ctx.lineTo(cx - r, cy);
        ctx.closePath();
        ctx.stroke();
      }
    }
  }

  // Physical hive CHAMBERS — the realised rooms (Nursery / Storage / Military /
  // Mutation), a type-tinted square with the owning colony's border. Present only when
  // the physical hive runs (world.hive); drawn in the static layer like nodes so fog
  // remembers them dimmed. Reuses the hive-node toggle.
  const hive = world.hive;
  if (settings.showHiveNodes && hive) {
    const inset = cell * 0.18;
    const size = Math.max(2, cell - inset * 2);
    ctx.lineWidth = Math.max(1, cell * 0.08);
    // Level pips: an upgraded chamber (chamberLevelAt > 1) reads its level as a row of
    // bright pips along the square's top edge — one per level — but only when the square
    // is large enough to render them legibly (LOD; they vanish gracefully when zoomed out).
    const showPips = size >= 10;
    const pip = Math.max(1.5, size * 0.16);
    for (let cid = 0; cid < world.colonies.count; cid++) {
      const n = hive.chamberCountOf(cid);
      if (n === 0) continue;
      const border = colonyColor(cid);
      for (let k = 0; k < n; k++) {
        const px = offX + hive.chamberXAt(cid, k) * cell + inset;
        const py = offY + hive.chamberYAt(cid, k) * cell + inset;
        ctx.fillStyle = CHAMBER_COLOR[hive.chamberTypeAt(cid, k)] ?? '#888';
        ctx.fillRect(px, py, size, size);
        ctx.strokeStyle = border;
        ctx.strokeRect(px + 0.5, py + 0.5, size - 1, size - 1);
        if (showPips) {
          const level = hive.chamberLevelAt(cid, k);
          ctx.fillStyle = 'rgba(255,255,255,0.85)';
          for (let p = 0; p < level; p++) {
            ctx.fillRect(px + pip * (0.5 + p * 1.6), py + pip * 0.5, pip, pip);
          }
        }
      }
    }
  }

  // FOG OF WAR overlay — drawn over the static terrain (walls / routes / hive /
  // storage) but UNDER the live creatures + the player's own colony, so:
  //   • UNEXPLORED tiles read as solid undiscovered dark;
  //   • EXPLORED-but-not-currently-visible tiles keep their remembered terrain but
  //     dimmed (the live-entity layers above are already gated to visible tiles, so
  //     no rival/wildlife shows there — you remember ground, not enemy positions);
  //   • VISIBLE tiles get no overlay (full render).
  if (fog) drawFog(ctx, fog, view, x0, y0, x1, y1);

  // Organisms — drawn as LAYERED CREATURES assembled from their genes, so wings /
  // armour / venom / mandibles / size evolve visibly on the canvas. The sprite is
  // pulled from the shared per-genome-snapshot cache (one assembly per distinct
  // profile, O(1) on hits) so thousands of organisms cost a handful of assemblies.
  //
  // LEVEL OF DETAIL: when a cell is tiny (or the cache is absent — sibling worlds
  // that omit it), draw a cheap dot sized gently by the genome's overall scale
  // (Size-evolution still shows) instead of a full bug. Only large-enough cells
  // pay the full-detail geometry+paint cost.
  const sprites = world.sprites;
  const genomes = world.genomes;
  const fullDetail = sprites !== undefined && cell >= 7;
  const obs = watch ? watch.observedCaste : -1;
  // Spend the full-detail budget in active-set order. Once it's exhausted, the
  // remaining cell>=7 organisms drop to the dot path (allocation-light, fill-cheap)
  // — stable because the active set's order is stable, so the same ids keep detail.
  let detailDrawn = 0;
  for (let i = 0; i < n; i++) {
    const id = active[i] as number;
    if ((entities.kind[id] as number) !== KIND.Organism) continue;
    const cid = entities.colony[id] as number;
    // Fog: you always see your OWN colony anywhere; rivals only on currently-sensed
    // tiles (you remember their ground, not their present positions).
    if (fog && cid !== PLAYER_COLONY &&
        !fog.isVisible(entities.posX[id] as number, entities.posY[id] as number)) continue;
    const caste = entities.caste[id] as number;
    const ox = watch ? watch.ox(id, entities.posX[id] as number) : (entities.posX[id] as number);
    const oy = watch ? watch.oy(id, entities.posY[id] as number) : (entities.posY[id] as number);
    const cx = offX + ox * cell + cell / 2;
    const cy = offY + oy * cell + cell / 2;
    // Generation overlay (render-only): colour by lineage AGE relative to each
    // organism's OWN colony — an un-evolved rival reads as all-newest, an evolved
    // player shows a mix as old generations die off and the newest spreads.
    const color = settings.showGenerations
      ? generationColor((world.colonies.generationAt(cid) as number) - (entities.generation[id] as number))
      : colonyColor(cid);

    // Species observation: dim everything that isn't the spotlighted caste.
    const dim = obs >= 0 && caste !== obs;
    ctx.globalAlpha = dim ? 0.16 : 1;

    // Draw at full detail only while the per-frame budget has room; otherwise the
    // dot fallback keeps thousands of zoomed-in bugs cheap.
    const drawDetail = fullDetail && detailDrawn < FULL_DETAIL_BUDGET;
    if (drawDetail) {
      detailDrawn++;
      const pref = entities.profileRef[id] as number;
      const sprite = sprites.getOrAssemble(pref, genomes.profile(pref), entities.caste[id] as Caste);
      // Face the direction of travel (smooth, per-caste turning) when we have it.
      const rot = watch ? watch.heading(id) : 0;
      paintCreature(ctx, buildCreatureGeometry(sprite, cell * 0.5, id), cx, cy, color, rot);
    } else {
      // Cheap LOD dot. Size tracks the genome's overall scale when we can read it,
      // so a bigger-Size lineage still draws bigger even at the dot level.
      let scale = 100;
      if (sprites !== undefined) {
        const pref = entities.profileRef[id] as number;
        scale = sprites.getOrAssemble(pref, genomes.profile(pref), entities.caste[id] as Caste).baseScale;
      }
      const r = Math.max(1.4, cell * 0.34 * (scale / 100));
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.fill();
    }

    // Carried-resource pellet — the worker is visibly hauling food home.
    if (cell >= 4 && (entities.cargo[id] as number) > 0) {
      const pr = Math.max(1, cell * 0.17);
      ctx.fillStyle = '#ffd24a';
      ctx.beginPath();
      ctx.arc(cx + cell * 0.24, cy - cell * 0.24, pr, 0, Math.PI * 2);
      ctx.fill();
    }

    // Spotlight ring on the observed caste.
    if (obs >= 0 && caste === obs) {
      ctx.globalAlpha = 1;
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = Math.max(1, cell * 0.08);
      ctx.beginPath();
      ctx.arc(cx, cy, Math.max(2.4, cell * 0.52), 0, Math.PI * 2);
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
  }

  // --- QUEEN ENTITY: a REAL immobile root unit (KIND.Queen) at the nest, drawn
  //     ABOVE the brood she sits among. Larger colony-coloured body + a golden crown;
  //     an HP ring appears once she takes damage and a pulsing red outline marks her
  //     when imperiled (heavy siege). She never moves (no watch smoothing) — drawn at
  //     her exact sim tile. Allocation-light; reuses colonyColor. ---
  for (let i = 0; i < n; i++) {
    const id = active[i] as number;
    if ((entities.kind[id] as number) !== KIND.Queen) continue;
    const cid = entities.colony[id] as number;
    // You always see your OWN queen anywhere; a rival queen only on a sensed tile.
    if (fog && cid !== PLAYER_COLONY &&
        !fog.isVisible(entities.posX[id] as number, entities.posY[id] as number)) continue;
    const qx = entities.posX[id] as number;
    const qy = entities.posY[id] as number;
    const cx = offX + qx * cell + cell / 2;
    const cy = offY + qy * cell + cell / 2;
    const hp = world.colonies.queenHpAt(cid);
    const maxHp = world.colonies.queenMaxHp;
    const imperiled = world.colonies.isQueenImperiled(cid);
    drawQueen(ctx, colonyColor(cid), cx, cy, cell, hp, maxHp, imperiled);
  }

  // Supply-bottleneck warnings — pulsing rings over heavy-traffic constrictions,
  // drawn ABOVE organisms so the alert is never hidden behind the brood.
  if (logistics && settings.showTrails) logistics.drawBottlenecks(ctx, view);

  // Courier motes (larva + gene transport) over the colony.
  if (logistics && settings.showTrails) logistics.drawCouriers(ctx, view);

  // --- WILDLIFE: the non-colony ecosystem, drawn ABOVE organisms so predator
  //     pressure reads at a glance. Distinct shape + neutral/hostile colour per
  //     species (never colonyColor). Territorial species also get a faint home
  //     ring so ownership is visible. They already move each tick, so motion /
  //     migration / predation read naturally with no extra work here. ---
  const wildlife = world.wildlife;
  for (let i = 0; i < n; i++) {
    const id = active[i] as number;
    if ((entities.kind[id] as number) !== KIND.Wildlife) continue;
    // Wildlife is the live ecosystem — only render where the player can sense it.
    if (fog && !fog.isVisible(entities.posX[id] as number, entities.posY[id] as number)) continue;
    const sp = wildlife.species[id] as number;
    const style = SPECIES_STYLE[sp];
    if (!style) continue;
    // Interpolated position (smooth inertial chase) when watch is present, else
    // the raw sim tile. Home/territory ring stays anchored at the sim home tile.
    const wox = watch ? watch.ox(id, entities.posX[id] as number) : (entities.posX[id] as number);
    const woy = watch ? watch.oy(id, entities.posY[id] as number) : (entities.posY[id] as number);
    const cx = offX + wox * cell + cell / 2;
    const cy = offY + woy * cell + cell / 2;
    const cfg = wildlife.cfgOf(id);

    // Faint territory ring at HOME for stationary territorial species (Spider /
    // Frog / Dragonfly — territoryRadius > 0 AND not a wide-ranging migrant).
    if (cfg && cfg.territoryRadius > 0 && !cfg.migrates) {
      const hx = offX + (wildlife.homeX[id] as number) * cell + cell / 2;
      const hy = offY + (wildlife.homeY[id] as number) * cell + cell / 2;
      ctx.strokeStyle = style.color;
      ctx.globalAlpha = 0.12;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(hx, hy, cfg.territoryRadius * cell, 0, Math.PI * 2);
      ctx.stroke();
      ctx.globalAlpha = 1;
    }

    drawWildlife(ctx, style, cx, cy, cell);
  }

  // --- APEX BOSS: a rare, menacing giant drawn ON TOP of everything, much larger
  //     than any unit, with a small HP bar so the player feels the threat. ---
  const apex = world.apex;
  if (apex && apex.bossAlive() && !(fog && !fog.isVisible(apex.bossX(), apex.bossY()))) {
    // Interpolated boss position (keyed by its entity id, same per-id store as
    // organisms/wildlife) so the giant glides rather than teleports; the HP bar
    // rides the same smoothed centre. Falls back to the raw sim tile sans watch.
    const bid = apex.bossEntityId();
    const bpx = apex.bossX();
    const bpy = apex.bossY();
    const box = watch ? watch.ox(bid, bpx) : bpx;
    const boy = watch ? watch.oy(bid, bpy) : bpy;
    const bx = offX + box * cell + cell / 2;
    const by = offY + boy * cell + cell / 2;
    const color = APEX_COLOR[apex.bossKind()] ?? '#ff4d6d';
    // Hunting-territory ring (centred on the boss's territory, not its live position) +
    // an FSM mode label, so the boss's INTENT — patrolling, hunting, retreating, migrating
    // — reads at a glance. Drawn UNDER the boss body so the giant stays legible on top.
    const trad = apex.bossTerritoryRadius();
    if (trad > 0) {
      const tcx = offX + apex.bossTerritoryX() * cell + cell / 2;
      const tcy = offY + apex.bossTerritoryY() * cell + cell / 2;
      const label = APEX_MODE_NAME[apex.bossMode()] ?? '';
      drawApexTerritory(ctx, color, tcx, tcy, trad * cell, label, bx, by, cell);
    }
    drawApex(ctx, color, bx, by, cell);
    drawApexHpBar(ctx, bx, by, cell, apex.bossHpVal(), apex.bossMaxHp());
  }

  // Player nest ring.
  const hx = world.colonies.homeXAt(0);
  const hy = world.colonies.homeYAt(0);
  const ncx = offX + hx * cell + cell / 2;
  const ncy = offY + hy * cell + cell / 2;
  // Soft halo under the ring so the home reads at a glance, even zoomed out.
  const glowR = Math.max(8, cell * 2.2);
  const glow = ctx.createRadialGradient(ncx, ncy, 0, ncx, ncy, glowR);
  glow.addColorStop(0, 'rgba(70,224,208,0.22)');
  glow.addColorStop(1, 'rgba(70,224,208,0)');
  ctx.fillStyle = glow;
  ctx.beginPath();
  ctx.arc(ncx, ncy, glowR, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = Math.max(1.5, cell * 0.16);
  ctx.beginPath();
  ctx.arc(ncx, ncy, Math.max(3, cell * 0.7), 0, Math.PI * 2);
  ctx.stroke();

  // ECOSYSTEM-EVENT WASH — a subtle full-map colour tint while a dynamic event runs
  // (flood blue, heat orange, …), reinforcing the HUD event banner. Atmospheric only;
  // drawn last over the played grid so it reads as weather over the whole scene.
  const events = world.events;
  if (events) {
    const tint = EVENT_TINT[events.activeEvent()];
    if (tint) {
      ctx.fillStyle = tint;
      ctx.fillRect(offX, offY, cell * W, cell * H);
    }
  }

  return view;
}

// Fog tones. Unexplored is near-opaque so undiscovered ground reads as void; the
// dim overlay is a translucent darken so remembered terrain stays legible but
// clearly "not live".
const FOG_UNEXPLORED = '#080a0e';
const FOG_DIM = 'rgba(8,10,14,0.55)';

/**
 * Paint the fog overlay across the visible tile range. Two run-batched passes over
 * the rows (one fillStyle each) keep it to O(visible tiles) with few state changes:
 * solid cover for unexplored tiles, a translucent darken for explored-not-visible.
 * Visible tiles are skipped entirely. Allocation-free.
 */
function drawFog(
  ctx: CanvasRenderingContext2D,
  fog: ExplorationMap,
  view: ViewBox,
  x0: number, y0: number, x1: number, y1: number,
): void {
  const { cell, offX, offY } = view;
  // Pass 1: solid cover over never-seen tiles, coalescing horizontal runs.
  ctx.fillStyle = FOG_UNEXPLORED;
  for (let y = y0; y < y1; y++) {
    let runStart = -1;
    for (let x = x0; x < x1; x++) {
      const cover = !fog.isExplored(x, y);
      if (cover && runStart < 0) runStart = x;
      else if (!cover && runStart >= 0) {
        ctx.fillRect(offX + runStart * cell, offY + y * cell, (x - runStart) * cell, cell);
        runStart = -1;
      }
    }
    if (runStart >= 0) ctx.fillRect(offX + runStart * cell, offY + y * cell, (x1 - runStart) * cell, cell);
  }
  // Pass 2: translucent darken over explored-but-not-currently-visible tiles.
  ctx.fillStyle = FOG_DIM;
  for (let y = y0; y < y1; y++) {
    let runStart = -1;
    for (let x = x0; x < x1; x++) {
      const dim = fog.isExplored(x, y) && !fog.isVisible(x, y);
      if (dim && runStart < 0) runStart = x;
      else if (!dim && runStart >= 0) {
        ctx.fillRect(offX + runStart * cell, offY + y * cell, (x - runStart) * cell, cell);
        runStart = -1;
      }
    }
    if (runStart >= 0) ctx.fillRect(offX + runStart * cell, offY + y * cell, (x1 - runStart) * cell, cell);
  }
}

/**
 * Draw one wildlife unit at (cx,cy). Each species has a recognizable silhouette;
 * flying species (bird, dragonfly) are lighter winged marks, ground species are
 * solid fills. Allocation-free — all primitive canvas calls.
 */
function drawWildlife(
  ctx: CanvasRenderingContext2D,
  style: SpeciesStyle,
  cx: number,
  cy: number,
  cell: number,
): void {
  const r = Math.max(1.6, cell * 0.34);
  ctx.fillStyle = style.color;
  ctx.strokeStyle = style.color;
  switch (style.shape) {
    case 'spider': {
      // Body + radiating legs.
      ctx.lineWidth = Math.max(1, cell * 0.08);
      ctx.beginPath();
      for (let k = 0; k < 4; k++) {
        const a = (Math.PI / 4) * k + Math.PI / 8;
        ctx.moveTo(cx - Math.cos(a) * r * 1.5, cy - Math.sin(a) * r * 1.5);
        ctx.lineTo(cx + Math.cos(a) * r * 1.5, cy + Math.sin(a) * r * 1.5);
      }
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(cx, cy, r * 0.7, 0, Math.PI * 2);
      ctx.fill();
      break;
    }
    case 'frog': {
      // Squat rounded body.
      ctx.beginPath();
      ctx.ellipse(cx, cy, r, r * 0.8, 0, 0, Math.PI * 2);
      ctx.fill();
      break;
    }
    case 'rat': {
      // Body + tail flick.
      ctx.beginPath();
      ctx.ellipse(cx, cy, r * 1.1, r * 0.7, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.lineWidth = Math.max(1, cell * 0.07);
      ctx.beginPath();
      ctx.moveTo(cx + r, cy);
      ctx.lineTo(cx + r * 2.2, cy + r * 0.6);
      ctx.stroke();
      break;
    }
    case 'beetle': {
      // Armoured shell: filled body with a dorsal split line.
      ctx.beginPath();
      ctx.ellipse(cx, cy, r * 0.95, r * 1.15, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = 'rgba(0,0,0,0.4)';
      ctx.lineWidth = Math.max(1, cell * 0.06);
      ctx.beginPath();
      ctx.moveTo(cx, cy - r);
      ctx.lineTo(cx, cy + r);
      ctx.stroke();
      break;
    }
    case 'bird':
    case 'dragonfly': {
      // Flying mark: a light winged glyph (translucent so it reads as airborne).
      ctx.globalAlpha = 0.9;
      ctx.lineWidth = Math.max(1, cell * 0.09);
      const wing = r * 1.4;
      ctx.beginPath();
      // Two wings as a shallow "M" / chevron.
      ctx.moveTo(cx - wing, cy - r * 0.2);
      ctx.quadraticCurveTo(cx - wing * 0.4, cy - r, cx, cy);
      ctx.quadraticCurveTo(cx + wing * 0.4, cy - r, cx + wing, cy - r * 0.2);
      ctx.stroke();
      // Small body.
      ctx.beginPath();
      ctx.arc(cx, cy, style.shape === 'dragonfly' ? r * 0.4 : r * 0.5, 0, Math.PI * 2);
      ctx.fill();
      ctx.globalAlpha = 1;
      break;
    }
    default: {
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.fill();
    }
  }
}

/**
 * Draw the apex's hunting-territory ring (a faint dashed circle centred on the territory)
 * plus its current FSM mode label above the boss, so its intent reads at a glance. Pure
 * render: a handful of canvas ops, no allocation (the dash pattern is a module const).
 */
function drawApexTerritory(
  ctx: CanvasRenderingContext2D,
  color: string,
  tcx: number,
  tcy: number,
  radiusPx: number,
  label: string,
  bx: number,
  by: number,
  cell: number,
): void {
  // Faint dashed territory ring.
  ctx.save();
  ctx.globalAlpha = 0.45;
  ctx.strokeStyle = color;
  ctx.lineWidth = Math.max(1, cell * 0.08);
  ctx.setLineDash(APEX_TERRITORY_DASH as number[]);
  ctx.beginPath();
  ctx.arc(tcx, tcy, radiusPx, 0, Math.PI * 2);
  ctx.stroke();
  ctx.restore();

  // Mode label above the boss (dark backing stroke for legibility on any terrain).
  if (label) {
    ctx.save();
    const fs = Math.max(9, Math.min(15, Math.round(cell)));
    ctx.font = `${fs}px system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'bottom';
    const ly = by - cell * 1.5; // clear of the boss body + its HP bar
    ctx.lineWidth = Math.max(2, fs * 0.3);
    ctx.strokeStyle = 'rgba(8,10,14,0.85)';
    ctx.strokeText(label, bx, ly);
    ctx.fillStyle = color;
    ctx.fillText(label, bx, ly);
    ctx.restore();
  }
}

/** Draw the apex boss: a large menacing star/burst, sized well above any unit. */
function drawApex(
  ctx: CanvasRenderingContext2D,
  color: string,
  cx: number,
  cy: number,
  cell: number,
): void {
  const R = Math.max(8, cell * 1.7);
  // Outer glow.
  ctx.globalAlpha = 0.25;
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(cx, cy, R * 1.1, 0, Math.PI * 2);
  ctx.fill();
  ctx.globalAlpha = 1;
  // Spiked body (8-point star) — the menacing silhouette.
  ctx.fillStyle = color;
  ctx.beginPath();
  const spikes = 8;
  for (let k = 0; k < spikes * 2; k++) {
    const rad = k % 2 === 0 ? R : R * 0.5;
    const a = (Math.PI / spikes) * k - Math.PI / 2;
    const px = cx + Math.cos(a) * rad;
    const py = cy + Math.sin(a) * rad;
    if (k === 0) ctx.moveTo(px, py);
    else ctx.lineTo(px, py);
  }
  ctx.closePath();
  ctx.fill();
  // Dark core + outline so it pops over any background.
  ctx.fillStyle = 'rgba(10,12,16,0.85)';
  ctx.beginPath();
  ctx.arc(cx, cy, R * 0.34, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = Math.max(1.5, cell * 0.14);
  ctx.beginPath();
  ctx.arc(cx, cy, R * 0.9, 0, Math.PI * 2);
  ctx.stroke();
}

/** Draw a small HP bar above the apex boss. */
function drawApexHpBar(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  cell: number,
  hp: number,
  maxHp: number,
): void {
  if (maxHp <= 0) return;
  const frac = Math.max(0, Math.min(1, hp / maxHp));
  const w = Math.max(20, cell * 3.2);
  const h = Math.max(3, cell * 0.28);
  const x = cx - w / 2;
  const y = cy - Math.max(10, cell * 2.1);
  ctx.fillStyle = 'rgba(10,12,16,0.8)';
  ctx.fillRect(x - 1, y - 1, w + 2, h + 2);
  ctx.fillStyle = '#3a3f4a';
  ctx.fillRect(x, y, w, h);
  ctx.fillStyle = frac > 0.5 ? '#ff5d5d' : frac > 0.25 ? '#ffb13d' : '#ff2d4d';
  ctx.fillRect(x, y, w * frac, h);
}

/**
 * Draw the colony's QUEEN entity: a larger rounded body in her colony's colour with a
 * darker stroke, a small golden crown above her, an HP ring when damaged, and a pulsing
 * red outline when imperiled. Allocation-light (a handful of canvas ops, no arrays). The
 * imperiled pulse reads wall-clock time (render-only — it touches no sim state).
 */
function drawQueen(
  ctx: CanvasRenderingContext2D,
  color: string,
  cx: number,
  cy: number,
  cell: number,
  hp: number,
  maxHp: number,
  imperiled: boolean,
): void {
  const R = Math.max(4, cell * 0.65);

  // Pulsing red outline first (under the body) when she is under heavy siege.
  if (imperiled) {
    const pulse = 0.5 + 0.5 * Math.sin(Date.now() / 180); // 0..1, render-only
    ctx.globalAlpha = 0.35 + 0.45 * pulse;
    ctx.strokeStyle = '#ff2d4d';
    ctx.lineWidth = Math.max(1.5, cell * 0.16);
    ctx.beginPath();
    ctx.arc(cx, cy, R + Math.max(2, cell * 0.18), 0, Math.PI * 2);
    ctx.stroke();
    ctx.globalAlpha = 1;
  }

  // Body — larger rounded disc in the colony colour with a darker stroke.
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(cx, cy, R, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = 'rgba(10,12,16,0.75)';
  ctx.lineWidth = Math.max(1, cell * 0.1);
  ctx.stroke();

  // HP ring: a faint full background arc + a red arc spanning the MISSING fraction,
  // so damage reads at a glance. Only drawn once she has actually taken a hit.
  if (maxHp > 0 && hp < maxHp) {
    const ringR = R + Math.max(2, cell * 0.22);
    const missing = Math.max(0, Math.min(1, (maxHp - hp) / maxHp));
    ctx.lineWidth = Math.max(1.5, cell * 0.12);
    // Faint full background.
    ctx.strokeStyle = 'rgba(255,255,255,0.18)';
    ctx.beginPath();
    ctx.arc(cx, cy, ringR, 0, Math.PI * 2);
    ctx.stroke();
    // Red arc proportional to the missing HP, starting at the top.
    ctx.strokeStyle = '#ff4d6d';
    ctx.beginPath();
    ctx.arc(cx, cy, ringR, -Math.PI / 2, -Math.PI / 2 + missing * Math.PI * 2);
    ctx.stroke();
  }

  // Golden crown: three little triangles (a zig-zag) sitting just above the body.
  const crownY = cy - R - Math.max(1.5, cell * 0.18);
  const cw = R * 1.1;                   // total crown width
  const spike = cw / 3;                 // width of each of the 3 spikes
  const ch = Math.max(2, cell * 0.34);  // crown height
  const x0 = cx - cw / 2;               // left base of the crown
  ctx.fillStyle = '#ffd24a';
  ctx.beginPath();
  for (let k = 0; k < 3; k++) {
    const px = x0 + k * spike;
    ctx.moveTo(px, crownY);
    ctx.lineTo(px + spike / 2, crownY - ch);
    ctx.lineTo(px + spike, crownY);
  }
  ctx.closePath();
  ctx.fill();
}

export { COLONY_COLORS, colonyColor, GENERATION_RAMP, generationColor };
